import React, { useState } from 'react'
import injectSheet from 'react-jss'
import FormWrapper from '../../../../../form-wrapper/form-wrapper'
import FormHolder from '../../../../../form-holder/form-holder'
import Footer from '../../../../../footer/footer'
import Button from 'pobble-components/dist/button'
import FormRow from '../../../../../form-row/form-row'
import FormLabel from '../../../../../form-label/form-label'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import { Formik } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import * as Yup from 'yup'
import ButtonElement from 'pobble-components/dist/button-element'

function usePasswordProgress(password) {
  return {
    min: password.length >= 9,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[\d]/.test(password),
    special: /[!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?]/.test(password)
  }
}

const Hint = injectSheet({
  hint: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 'bold'
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '100%',
    backgroundColor: 'var(--gray---)',
    flexShrink: '0',
    marginRight: '6px',
    marginLeft: '2px'
  },
  icon: {
    fontSize: '10px',
    marginRight: '4px'
  },
  complete: {
    color: 'var(--green)',
    fontWeight: 'bold'
  }
})(({ classes, children, complete }) => {
  return (
    <li className={`${classes.hint} ${complete ? classes.complete : ''}`}>
      {!complete && <span className={classes.dot} />}
      {!!complete && (
        <FontAwesomeIcon className={classes.icon} icon={faCheck} />
      )}
      {children}
    </li>
  )
})

const PasswordHints = injectSheet({
  hints: {
    listStyle: 'none',
    margin: '8px 0 24px',
    font: 'var(--body--)',
    color: 'var(--gray---)',
    padding: '0',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    textAlign: 'left',
    columnGap: '16px',
    rowGap: '4px'
  }
})(({ classes, password }) => {
  const progress = usePasswordProgress(password)

  return (
    <ul className={classes.hints}>
      <Hint complete={progress.lower}>Lowercase letter</Hint>
      <Hint complete={progress.upper}>Uppercase letter</Hint>
      <Hint complete={progress.number}>Number</Hint>
      <Hint complete={progress.special}>Special character</Hint>
      <Hint complete={progress.min}>Minimum 9 characters</Hint>
    </ul>
  )
})

const Form = injectSheet({
  showHide: {
    font: 'var(--body--)',
    color: 'var(--gray--)',
    margin: '4px 0 8px',
    textDecoration: 'underline'
  }
})(({ classes, onSubmit, pending, email }) => {
  const [showPassword, setShowPassword] = useState(false)
  return (
    <FormWrapper pending={pending}>
      <Formik
        onSubmit={onSubmit}
        initialValues={{
          password: '',
          repeatPassword: ''
        }}
        validationSchema={Yup.object().shape({
          password: Yup.string()
            .required('Please provide your password')
            .min(9, 'Minimum password length is 9 characters')
            .matches(
              /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?]).{9,64}$/,
              {
                message:
                  'Password should contain at least one uppercase, one lowercase, one number and one special character'
              }
            ),
          repeatPassword: Yup.string()
            .required('Please confirm your password')
            .oneOf([Yup.ref('password'), null], 'Passwords do not match')
        })}>
        {formikProps => {
          const {
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            errors,
            touched,
            isValid
          } = formikProps
          return (
            <form onSubmit={handleSubmit}>
              <FormHolder>
                <FormRow>
                  <FormLabel>Email address</FormLabel>
                  <FormInput
                    center
                    disabled
                    type="email"
                    value={email}
                    inputProps={{
                      autoComplete: 'username email'
                    }}
                  />
                </FormRow>
                <FormRow>
                  <FormLabel>Password</FormLabel>
                  <FormInput
                    center
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    validationError={touched.password && errors.password}
                    inputProps={{
                      autoComplete: 'new-password'
                    }}
                  />
                  {values.password && (
                    <ButtonElement
                      onClick={() => {
                        setShowPassword(!showPassword)
                      }}
                      className={classes.showHide}>
                      {showPassword && 'Hide password'}
                      {!showPassword && 'Show password'}
                    </ButtonElement>
                  )}
                  <PasswordHints password={values.password} />
                </FormRow>
                <FormRow>
                  <FormLabel>Confirm password</FormLabel>
                  <FormInput
                    center
                    type="password"
                    name="repeatPassword"
                    value={values.repeatPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    validationError={
                      touched.repeatPassword && errors.repeatPassword
                    }
                    inputProps={{
                      autoComplete: 'new-password'
                    }}
                  />
                </FormRow>
              </FormHolder>
              <Footer dense>
                <Button
                  disabled={!isValid}
                  darkBlue
                  tall
                  wide
                  type="submit"
                  className={'metrics__sign-up-password'}>
                  Next
                </Button>
              </Footer>
            </form>
          )
        }}
      </Formik>
    </FormWrapper>
  )
})

export default Form
